import React, { Suspense } from 'react';

import { Button } from '@components/Button';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { MobxZodTypes } from '@monoid-dev/mobx-zod-form';
import { ReactForm } from '@monoid-dev/mobx-zod-form-react';

const LazyReporter = React.lazy(() => import('@monoid-dev/zod-monaco-reporter'));

export const DebugForm = function <T extends MobxZodTypes>({
  form,
  hidden = import.meta.env.PROD,
}: {
  form: ReactForm<T>;
  hidden?: boolean;
}) {
  const { renderDialog, confirm } = useConfirmDialog({
    actionableDialogProps: {
      size: 'lg',
    },
  });

  if (hidden) return null;

  const renderTitle = (title: string) => {
    return (
      <div style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>
        <b>{title}</b>
      </div>
    );
  };

  return (
    <>
      <Button
        size="small"
        color="accent-outlined"
        text="Debug"
        type="button"
        onClick={async () => {
          const rawResult = form.schema.safeParse(form.looseInput.success && form.looseInput.data);

          await confirm({
            title: 'Debug Form',
            message: (
              <div style={{ height: 550 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  {renderTitle('Basic Info:')}

                  {renderTitle('Raw Input:')}

                  <div style={{ whiteSpace: 'pre', overflow: 'auto' }}>
                    {JSON.stringify(
                      {
                        isDirty: form.isDirty,
                        isSubmitting: form.isSubmitting,
                        submitCount: form.submitCount,
                      },
                      undefined,
                      2,
                    )}
                  </div>

                  <div style={{ whiteSpace: 'pre', overflow: 'auto' }}>
                    {JSON.stringify(form.rawInput, undefined, 2)}
                  </div>
                </div>

                {renderTitle('Error Report:')}
                <Suspense fallback={<>Loading reporter...</>}>
                  <LazyReporter
                    schema={form.schema}
                    value={form.looseInput.success && form.looseInput.data}
                  />
                </Suspense>

                {renderTitle('Raw Result:')}

                <div style={{ whiteSpace: 'pre', overflow: 'auto' }}>
                  {JSON.stringify(rawResult, undefined, 2)}
                </div>

                {renderTitle('Raw Error Formatted:')}

                <div style={{ whiteSpace: 'pre', overflow: 'auto' }}>
                  {JSON.stringify(
                    rawResult.success === false ? rawResult.error.format() : null,
                    undefined,
                    2,
                  )}
                </div>
              </div>
            ),
          });
        }}
      />
      {renderDialog()}
    </>
  );
};
